import {Formik, FormikConfig} from 'formik'
import {FC, useCallback, useMemo, useState} from 'react'
import {useMutation, useQuery} from 'react-query'
import {Outlet, useLocation, useNavigate, useParams} from 'react-router-dom'

import {AreaSelectArea} from '../../../api/area/types'
import {BookingQueries} from '../../../api/booking'
import {commonServiceBookingDataAdapter, commonServiceBookingPayByTokenAdapter, PayByTokenServiceBookingDraft, removeFormFromBookingData} from '../../../api/booking/helpers'
import {Instructor, ServiceItem} from '../../../api/booking/types'
import {getEntityDataByIdFromAppearanceSettings} from '../../../api/helpers/data'
import {PromoError} from '../../../api/promo-codes/types'
import {handlePromoPayError} from '../../../components/promo-code-field/handle-promo-pay-error'
import {handleAddonInvalidError} from '../../../helpers/addon-error-message'
import {isContactBlockedError, showBlockedMessage} from '../../../helpers/blocked-member'
import {usePassNotValidHandler} from '../../../helpers/use-pass-not-valid-handler'
import {auth} from '../../../utils/firebase'
import {useLuxon} from '../../../utils/i18n'
import {useModalActions} from '../../../utils/modals'
import {defaultUserValues, IntegratedSignUpFormType} from '../../auth/forms/integrated-sign-up-form'
import {showChargeRetryHintIfDegraded} from '../../payments/helpers/charge-retry-hint'
import {useHandleDraftEntityStatusChange} from '../../payments/hooks/handle-draft-entity-status-change.hook'
import {usePaymentProvider} from '../../payments/hooks/payment-provider-form.provider'
import {useBookingLoader} from '../hooks'
import {BookingService} from '../index'
import {AppearanceEntityGate} from './appearance-entity-gate'
import {RedirectionComponent} from './components/redirection-component'
import {CreateServiceBookingFormType} from './create-service-booking-form'
import {useOpenBookingValidationModal} from './hooks/open-booking-validation-modal.hook'
import {resolveMinParticipants} from './min-participants'
import {BookingStateParticipant} from './types'

export type CreateInstructorBookingFormType = IntegratedSignUpFormType & {
  date: string
  venueId?: string
  areaId?: string
  participants: BookingStateParticipant[]
  hasSpecialRequests: boolean
  specialRequest: string
  bookingType: 'service' | 'space' | 'teacher'
  entity: 'service' | 'teacher'
  paymentMethodId: string | null
  maxParticipants: number
  minParticipants: number
  isJustTheSpace: boolean
  serviceId: string | null
  service: ServiceItem | null
  teacherId?: string | null
  teacher: Instructor | null
  area: AreaSelectArea | null
  userId: string | null | undefined
  time: null[] | [number | null, number | null]
  useForParticipants?: boolean
  coverEquipment?: boolean
  paymentType?: 'oneTimePayment' | 'payNow' | 'payOnLocation'
  flowStartsFromService?: boolean
  promoCode?: string | null
  promoRejection?: PromoError | null
}

type Params = {
  venueId: string
  instructorId?: string
  serviceId?: string
}

const CreateInstructorBookingFormInner: FC = () => {
  const luxon = useLuxon()
  const [entityId, setEntityId] = useState<string | null>(null)
  const {closeAllModals} = useModalActions()
  const navigate = useNavigate()
  const {venueId, instructorId, serviceId} = useParams<Params>()
  const {isLoading, setIsLoading} = useBookingLoader()
  const {openPaymentForm} = usePaymentProvider()
  const {data: priceDataObjects} = useQuery(BookingQueries.calculateBookingPriceQuery({enabled: false}))
  const {mutate: createDraftBooking} = useMutation(BookingService.mutations.createServiceBookingDraftMutation({}))
  // const [mustGoToHomePage, setMustGoToHomePage] = useState(false)

  // const initialUserId = useMemo(() => auth.currentUser?.uid, [])

  if (!venueId || (!instructorId && !serviceId)) {
    return null
  }

  const location = useLocation()
  const state = location.state as Record<string, unknown> | undefined

  let instructor: Instructor | null = null
  let serviceData: ServiceItem | null = null
  let flowStartsFromService = false

  if (instructorId) {
    instructor = getEntityDataByIdFromAppearanceSettings(venueId, instructorId) as Instructor
  }
  if (serviceId) {
    flowStartsFromService = true
    serviceData = getEntityDataByIdFromAppearanceSettings(venueId, serviceId) as ServiceItem
  }

  // useEffect(() => {
  //   setMustGoToHomePage(true)
  // }, [auth.currentUser?.uid])

  const {mutate: createServiceBooking} = useMutation(BookingService.mutations.createInstructorBookingMutation({}))
  const {mutate: createServiceBookingPayByToken} = useMutation(
    BookingService.mutations.createInstructorBookingPayByTokenMutation({}),
  )
  const {setStartRefetch} = useHandleDraftEntityStatusChange({
    entity: 'booking',
    entityId,
    isLoading,
    setIsLoading,
  })
  const {openBookingValidationModal} = useOpenBookingValidationModal()
  const handlePassNotValid = usePassNotValidHandler()
  const initialValues = useMemo<CreateServiceBookingFormType>(
    () => ({
      area: null,
      participants: [],
      entity: 'teacher',
      // eslint-disable-next-line
      venueId: venueId!,
      minParticipants: resolveMinParticipants(serviceData),
      specialRequest: '',
      time: [null, null],
      teacher: instructor,
      service: serviceData,
      isJustTheSpace: false,
      paymentMethodId: null,
      flowStartsFromService,
      splitPayment: false,
      bookingType: 'teacher',
      hasSpecialRequests: false,
      maxParticipants: Infinity,
      serviceId: serviceId || null,
      teacherId: instructorId || null,
      date: luxon.now().toFormat('dd/MM/yyyy'),
      ...defaultUserValues,
    }),
    [venueId, instructor, serviceData, serviceId, instructorId],
  )

  if (state?.teacher) {
    // @ts-ignore
    initialValues.teacher = state.teacher
    // @ts-ignore
    // eslint-disable-next-line
    initialValues.teacherId = state.teacher.uid
  }

  const handleSubmit = useCallback<FormikConfig<CreateInstructorBookingFormType>['onSubmit']>((values, formikHelpers) => {
    const {setFieldValue} = formikHelpers
    if (!priceDataObjects) {
      return
    }
    // if (initialUserId !== auth.currentUser?.uid && serviceData?.accessForAnyone !== false) {
    //   return openBookingValidationModal({type: 'service'})
    // }
    const specialRequestElement = typeof document !== 'undefined' ? (document.getElementById('specialRequest') as HTMLInputElement | HTMLTextAreaElement | null) : null
    const specialRequestFromDom = specialRequestElement?.value ?? ''
    const valuesWithSpecialRequest: CreateInstructorBookingFormType = values.hasSpecialRequests
      ? {...values, specialRequest: specialRequestFromDom}
      : {...values, specialRequest: ''}
    const isPayNow = !!(values.paymentMethodId && values.paymentType !== 'payOnLocation')
    const createBookingFunction = isPayNow ? createServiceBookingPayByToken : createServiceBooking
    const dataForAdapter = {
      bookingState: {
        ...valuesWithSpecialRequest,
        userId: auth.currentUser?.uid,
      },
      priceDataObjects,
    }
    let adaptedData = null
    let dataForDraft: PayByTokenServiceBookingDraft | null = null
    if (values.paymentType !== 'payOnLocation') {
      adaptedData = commonServiceBookingPayByTokenAdapter(dataForAdapter)
      dataForDraft = adaptedData
    } else {
      adaptedData = commonServiceBookingDataAdapter(dataForAdapter)
    }
    if (values.paymentType === 'oneTimePayment') {
      if (dataForDraft === null) {
        return
      }
      const dataForDraftToUse = dataForDraft as PayByTokenServiceBookingDraft
      const submitDraft = (data: any, isRetry = false) => {
        createDraftBooking(
          data,
          {
            onSuccess: () => {
              setStartRefetch(true)
              setEntityId(dataForDraftToUse.bookingId || null)
              openPaymentForm && openPaymentForm({
                entity: 'booking',
                bookingId: dataForDraftToUse.bookingId,
                // totalPrice: dataForDraftToUse.data.totalPrice,
                startProcessCallback: () => setIsLoading(true),
                totalPrice: dataForDraftToUse.data.splitPayment ? dataForDraftToUse.data.paidAmount : dataForDraftToUse.data.totalPrice,
              })
            },
            onError: (error: any) => {
              if (error?.response?.status === 400 && (error?.response?.data?.code === 'FORM_INACTIVE' || error?.response?.data?.message === 'Form is inactive or archived') && !isRetry) {
                submitDraft(removeFormFromBookingData(data), true)
                return
              }
              if (handleAddonInvalidError(error)) {
                return
              }
              if (handlePromoPayError(error, setFieldValue, 'calculateBookingPriceQuery')) {
                return
              }
              if (handlePassNotValid(error, {participants: values.participants, setFieldValue})) {
                return
              }
              if (isContactBlockedError(error)) {
                return showBlockedMessage()
              }
              if (error?.response?.status === 400 && error?.response?.data?.type === 'TIME_SLOT_NOT_AVAILABLE') {
                setFieldValue('time', [null, null])
                openBookingValidationModal({type: 'service'})
              }
            },
          }
        )
      }
      submitDraft(adaptedData)
    } else {
      setEntityId(null)
      setIsLoading(true)
      const submitBooking = (data: any, isRetry = false) => {
        createBookingFunction(
          data,
          {
            onSuccess: (booking) => {
              closeAllModals()
              setIsLoading(false)
              const uid = booking.uid ?? (booking as { bookingId?: string }).bookingId
              if (venueId && uid) {
                navigate(`/${venueId}/bookings/${uid}`, {replace: true})
              }
            },
            onError: (error: any) => {
              console.log(error)
              if (error?.response?.status === 400 && (error?.response?.data?.code === 'FORM_INACTIVE' || error?.response?.data?.message === 'Form is inactive or archived') && !isRetry) {
                submitBooking(removeFormFromBookingData(data), true)
                return
              }
              setIsLoading(false)
              if (handleAddonInvalidError(error)) {
                return
              }
              if (handlePromoPayError(error, setFieldValue, 'calculateBookingPriceQuery')) {
                return
              }
              if (handlePassNotValid(error, {participants: values.participants, setFieldValue})) {
                return
              }
              if (isContactBlockedError(error)) {
                return showBlockedMessage()
              }
              if (error.response?.status === 409) {
                setFieldValue('time', [null, null])
                return openBookingValidationModal({type: 'service'})
              }
              if (isPayNow) {
                showChargeRetryHintIfDegraded(venueId)
              }
            },
          },
        )
      }
      submitBooking(adaptedData)
    }
  }, [venueId, priceDataObjects, createServiceBooking, createServiceBookingPayByToken, setIsLoading, navigate, closeAllModals, handlePassNotValid])

  return (
    <Formik
      validateOnChange={false}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <RedirectionComponent>
        <Outlet />
      </RedirectionComponent>
    </Formik>
  )
}

export const CreateInstructorBookingForm: FC = () => (
  <AppearanceEntityGate entityParams={['instructorId', 'serviceId']}>
    <CreateInstructorBookingFormInner />
  </AppearanceEntityGate>
)
